import { useState } from 'react';
import { X, Forward, Bookmark, Trash2 } from 'lucide-react';
import { useSelectionStore } from '@/store/selectionStore';
import { messagesApi } from '@/api/messages';
import ForwardModal from './ForwardModal';

interface BulkActionBarProps {
  /** True if all selected messages are authored by the current user
   *  (server-side bulk-delete only deletes own DMs anyway, but for channel
   *  messages we can let global admins delete others' messages too). */
  canDelete: boolean;
  /** Called after a successful bulk action so the view can refresh. */
  onChanged?: () => void;
}

/**
 * Floating action bar shown above the message list while in selection mode.
 * Renders count + Forward / Save-to-personal / Delete buttons.
 */
export default function BulkActionBar({ canDelete, onChanged }: BulkActionBarProps) {
  const active = useSelectionStore((s) => s.active);
  const ids = useSelectionStore((s) => s.ids);
  const scope = useSelectionStore((s) => s.scope);
  const exit = useSelectionStore((s) => s.exit);

  const [forwardOpen, setForwardOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!active || !scope || ids.size === 0) return null;

  const idsArray = Array.from(ids);
  // Determine payload shape: channel scope → messageIds, dm scope → dmIds.
  const payload: { messageIds?: string[]; dmIds?: string[] } =
    scope.kind === 'channel' ? { messageIds: idsArray } : { dmIds: idsArray };

  const handleSave = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await messagesApi.bulkSaveToPersonal(payload);
      // simple feedback via console; UI toast is a future polish
      console.log(`[bulk] saved ${res.saved}, failed ${res.failed.length}`);
    } catch (e) {
      console.warn('[bulk] save failed', e);
    } finally {
      setBusy(false);
      exit();
      onChanged?.();
    }
  };

  const handleDelete = async () => {
    if (busy) return;
    if (!confirm(`Удалить ${ids.size} сообщений?`)) return;
    setBusy(true);
    try {
      const res = await messagesApi.bulkDelete(payload);
      console.log(`[bulk] deleted ${res.deleted}, failed ${res.failed.length}`);
    } catch (e) {
      console.warn('[bulk] delete failed', e);
    } finally {
      setBusy(false);
      exit();
      onChanged?.();
    }
  };

  return (
    <>
      <div className="flex items-center gap-2 px-3 py-2 bg-surface-900 border-b border-surface-800">
        <button
          onClick={exit}
          className="p-1.5 rounded hover:bg-surface-800 text-surface-300"
          title="Отмена"
        >
          <X size={18} />
        </button>
        <span className="text-sm text-surface-200 flex-1">{ids.size} выбрано</span>

        {/* Forward */}
        {scope.kind === 'channel' && (
          <button
            onClick={() => setForwardOpen(true)}
            disabled={busy}
            className="p-1.5 rounded hover:bg-surface-800 text-surface-300 disabled:opacity-50"
            title="Переслать"
          >
            <Forward size={18} />
          </button>
        )}

        <button
          onClick={handleSave}
          disabled={busy}
          className="p-1.5 rounded hover:bg-surface-800 text-surface-300 disabled:opacity-50"
          title="В «Сохранённые»"
        >
          <Bookmark size={18} />
        </button>

        {canDelete && (
          <button
            onClick={handleDelete}
            disabled={busy}
            className="p-1.5 rounded hover:bg-surface-800 text-red-400 disabled:opacity-50"
            title="Удалить"
          >
            <Trash2 size={18} />
          </button>
        )}
      </div>

      {forwardOpen && scope.kind === 'channel' && (
        <ForwardModal
          messageId={idsArray[0]}
          messageIds={idsArray}
          preview={`${idsArray.length} сообщений`}
          onClose={() => { setForwardOpen(false); exit(); onChanged?.(); }}
        />
      )}
    </>
  );
}
